import axios from "axios";
import { Credentials, EnterCode, ResetPassword, UserDetails } from "../redux/types";

// =============================================================================
// Configuration
// =============================================================================
const MEDLEY_API = process.env.REACT_APP_BACKEND_URL as string;

// =============================================================================
// Types
// =============================================================================
export type Response<T> = {
  status: any;
  refreshToken: any;
  message: any;
  value: T | null;
  error: string | null;
};

export type SearchUserPayload = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

export type PrivacyDeletePayload = {
  mpID: string;
  cognitoId: string;
};

export type Message = {
  message: string;
};

export type SigninPayload = {
  medleyUserEmail: string;
  password: string;
};

export type RefreshTokenPayload = {
  refreshToken: string;
};

type SearchPayload = {
  email?: string;
  mobilePhoneNumber?: string;
};

// =============================================================================
// Axios Instance
// =============================================================================
export const axiosApiInstance = axios.create({
  baseURL: MEDLEY_API,
  headers: { "Content-Type": "application/json" },
});

/** JWT from localStorage: JSON `{ idToken }`, raw string, or quoted string */
function idTokenFromStoredValue(storedToken: string): string | undefined {
  try {
    const parsed = JSON.parse(storedToken) as unknown;
    if (typeof parsed === "string" && parsed.length > 0) {
      return parsed;
    }
    if (parsed && typeof parsed === "object" && parsed !== null && "idToken" in parsed) {
      const id = (parsed as { idToken?: unknown }).idToken;
      if (typeof id === "string" && id.length > 0) {
        return id;
      }
    }
  } catch {
    /* not JSON — treat as literal token */
  }
  let literal = storedToken.trim();
  if (literal.length >= 2 && literal.startsWith('"') && literal.endsWith('"')) {
    literal = literal.slice(1, -1);
  }
  return literal.length > 0 ? literal : undefined;
}

// Add Authorization header to all requests
axiosApiInstance.interceptors.request.use((config) => {
  try {
    const storedToken = localStorage.getItem("idToken");
    const token = storedToken ? idTokenFromStoredValue(storedToken) : undefined;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (error) {
    console.error("Error setting auth header:", error);
  }
  return config;
});

let isRefreshing = false;
let pendingRequests: Array<(token: string) => void> = [];

function getStoredRefreshToken(): string {
  try {
    const stored = localStorage.getItem("redux_login");
    if (stored) {
      const parsed = JSON.parse(stored) as { refreshToken?: string };
      return parsed?.refreshToken ?? "";
    }
  } catch { /* ignore */ }
  return "";
}

function updateStoredLoginTokens(newIdToken: string, newRefreshToken?: string): void {
  try {
    const storedLogin = localStorage.getItem("redux_login");
    if (!storedLogin) {
      return;
    }
    const loginState = JSON.parse(storedLogin);
    loginState.idToken = newIdToken;
    if (newRefreshToken) {
      loginState.refreshToken = newRefreshToken;
    }
    localStorage.setItem("redux_login", JSON.stringify(loginState));
  } catch { /* ignore */ }
}

function clearAuthAndRedirect(): void {
  pendingRequests = [];
  localStorage.removeItem("idToken");
  localStorage.removeItem("redux_login");
  globalThis.location.href = "/";
}

async function performTokenRefresh(): Promise<string> {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  const response = await axiosApiInstance.post<Response<Credentials>>(
    "/authentication/regenerate-tokens",
    { refreshToken }
  );
  const data = response.data as any;

  localStorage.removeItem("idToken");
  localStorage.setItem("idToken", JSON.stringify(data));

  const newToken = data?.idToken as string | undefined;
  if (!newToken) {
    throw new Error("Token refresh returned no idToken");
  }

  updateStoredLoginTokens(newToken, data.refreshToken);
  return newToken;
}

axiosApiInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isAuthEndpoint = (originalRequest.url as string)?.includes("/authentication/");

    if (error.response?.status !== 401 || originalRequest._retry || isAuthEndpoint) {
      throw error;
    }

    originalRequest._retry = true;

    if (isRefreshing) {
      return new Promise((resolve) => {
        pendingRequests.push((newToken: string) => {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          resolve(axiosApiInstance(originalRequest));
        });
      });
    }

    isRefreshing = true;

    try {
      const newToken = await performTokenRefresh();
      pendingRequests.forEach((cb) => cb(newToken));
      pendingRequests = [];
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return axiosApiInstance(originalRequest);
    } catch (refreshError) {
      clearAuthAndRedirect();
      throw refreshError;
    } finally {
      isRefreshing = false;
    }
  }
);

// =============================================================================
// Helper Functions
// =============================================================================
const authHeader = (token?: string) => ({
  Authorization: `Bearer ${token}`,
});

// =============================================================================
// Authentication API
// =============================================================================
export const userLogin = async (body: SigninPayload) => {
  const response = await axiosApiInstance.post<Response<Credentials>>(
    "/authentication/sign-in",
    body
  );
  return response.data;
};

export const forgotPassword = async (body: any) => {
  const response = await axiosApiInstance.post<Response<string>>(
    "/authentication/forgot-password",
    body
  );
  return response.data;
};

export const resetPassword = async (body: SigninPayload) => {
  const response = await axiosApiInstance.post<Response<ResetPassword>>(
    "/authentication/reset-password",
    body
  );
  return response.data;
};

export const verificationCode = async (body: any) => {
  const response = await axiosApiInstance.post<Response<EnterCode>>(
    "/authentication/verify-confirmation-code",
    body
  );
  return response.data;
};

export const refreshTokenLogin = async (body: RefreshTokenPayload) => {
  const response = await axiosApiInstance.post<Response<Credentials>>(
    "/authentication/regenerate-tokens",
    body
  );
  return response.data;
};

// =============================================================================
// Guest Experience API
// =============================================================================
export const basicSearch = async (body: SearchPayload, token?: string) => {
  const response = await axiosApiInstance.post<Response<any>>(
    "/guestexperience/customer/search",
    body,
    { headers: authHeader(token) }
  );
  return response.data;
};

export const advancedSearch = async (body: SearchPayload, token?: string) => {
  const response = await axiosApiInstance.post<Response<Credentials>>(
    "/guestexperience/customer/advancedsearch",
    body,
    { headers: authHeader(token) }
  );
  return response.data;
};

export const userDetails = async (id: any, token?: string) => {
  const response = await axiosApiInstance.get<Response<UserDetails>>(
    `/guestexperience/customer/${id}`,
    { headers: authHeader(token) }
  );
  return response.data;
};

export const userUpdate = async (mpId: string, body: any) => {
  const response = await axiosApiInstance.put<Response<string>>(
    `/guestexperience/customer/${mpId}`,
    body
  );
  return response;
};

// =============================================================================
// Master Profile API
// =============================================================================
export type DnssUpdatePayload = {
  gpcSignal: boolean;
};

export const masterProfileDnssUpdate = async (mpId: string, body: DnssUpdatePayload) => {
  const response = await axiosApiInstance.put<Response<string>>(
    `/guestexperience/customer/${mpId}/dnss`,
    body
  );
  return response.data;
};

export type UpdateAccountValues = {
  firstName?: string;
  lastName?: string;
  email?: string;
  mobilePhoneNumber?: string;
  birthday?: string;
  birthdayUpdated?: boolean;
};

export type UpdateAccountPayload = {
  updateValues: UpdateAccountValues | Record<string, unknown>;
};

/**
 * Update master profile account details.
 */
export const masterProfileUpdate = async (mpId: string, body: UpdateAccountPayload) => {
  const response = await axiosApiInstance.put<Response<string>>(
    `/guestexperience/customer/${mpId}`,
    body
  );
  return response.data;
};

export type LockAccountPayload = {
  zaxbysId: string;
};

export const masterProfileLockRemoveUpdate = async (body: LockAccountPayload) => {
  const response = await axiosApiInstance.put<Response<string>>(
    "/guestexperience/customer/removelock",
    body
  );
  return response.data ?? { status: response.status, value: null, error: null, message: null, refreshToken: null };
};

/**
 * Admin-initiated password reset for a user.
 * Sends a password reset email to the user.
 * cognitoId is passed as a URL parameter.
 */
export const masterProfileResetPassword = async (cognitoId: string) => {
  const response = await axiosApiInstance.post<Response<string>>(
    `/guestexperience/customer/${cognitoId}/resetpassword`
  );
  return response.data;
};

export const masterProfileDisableEnableUpdate = async (mpId: any, body: any) => {
  const response = await axiosApiInstance.put<Response<string>>(
    `/guestexperience/customer/${mpId}/status`,
    body
  );
  return response.data;
};

export const masterProfilePrivacyDelete = async (mpId: any) => {
  const response = await axiosApiInstance.delete<Response<string>>(
    `/guestexperience/customer/${mpId}`
  );
  return response.data;
};

/** Generate and download Right to Know report as Excel file. */
export const masterProfileRightToKnowReport = async (mpId: string, customerName?: string) => {
  const response = await axiosApiInstance.get(
    `/datasubjectrequest/${mpId}/righttoknow`,
    {
      responseType: "blob",
      headers: {
        Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      },
    }
  );

  // Create download link for the Excel file
  const blob = new Blob([response.data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = globalThis.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;

  const [date] = new Date().toISOString().split("T");
  const safeName =
    customerName?.replaceAll(/[^a-zA-Z0-9]/g, "_") ?? mpId;
  link.download = `RightToKnow_${safeName}_${date}.xlsx`;

  document.body.appendChild(link);
  link.click();

  link.remove();
  globalThis.URL.revokeObjectURL(url);

  return { status: response.status, message: "Report downloaded successfully" };
};

// =============================================================================
// Vendor API
// =============================================================================
export const vendorDelete = async (vendorKey: string, customerVendorId: string) => {
  const response = await axiosApiInstance.delete<Response<string>>(
    `/guestexperience/customer/${vendorKey}/${customerVendorId}`
  );
  return response.data;
};

export const vendorDNSSUpdate = async (vendorKey: string, customerVendorId: string, body: any) => {
  const response = await axiosApiInstance.put<Response<string>>(
    `/guestexperience/customer/${customerVendorId}/dnss`,
    body
  );
  return response.data;
};

export const vendorStatusUpdate = async (vendorKey: string, customerVendorId: string, body: { status: boolean }) => {
  const response = await axiosApiInstance.put<Response<string>>(
    `/guestexperience/customer/${customerVendorId}/status`,
    body
  );
  return response.data;
};

export type VendorUpdateValues = {
  firstName?: string;
  lastName?: string;
  email?: string;
  mobilePhoneNumber?: string;
  birthday?: string;
  birthdayUpdated?: boolean;
};

export type VendorUpdatePayload = {
  updateValues: VendorUpdateValues;
};

/**
 * Update vendor profile.
 * Endpoint: PUT /guestexperience/customer/{vendorKey}/{customerVendorId}
 */
export const vendorUpdate = async (vendorKey: string, customerVendorId: string, body: VendorUpdatePayload) => {
  const response = await axiosApiInstance.put<Response<string>>(
    `/guestexperience/customer/${vendorKey}/${customerVendorId}`,
    body
  );
  return response.data;
};

export const vendorRequest = async (id: any, vendorKey: any, token?: string) => {
  const response = await axiosApiInstance.get<Response<UserDetails>>(
    `/guestexperience/customer/${vendorKey}/${id}`,
    { headers: authHeader(token) }
  );
  return response.data;
};

// =============================================================================
// RS Admin API
// =============================================================================
export const rsAdmin = async (storeId: any, token?: string) => {
  const response = await axiosApiInstance.get<Response<any>>(
    `/rsadmin/store/${storeId}`,
    { headers: authHeader(token) }
  );
  return response.data;
};

export const rsAdminUpdate = async (body: any) => {
  const response = await axiosApiInstance.put<Response<string>>(
    "/rsadmin/store",
    body
  );
  return response.data;
};
